<script setup lang="ts">
import UIBanner from '@/shared/ui/Banner';
import UIBreadcrumbs from '@/shared/ui/Breadcrumbs/index';
import type { IBreadcrumbs } from '@/shared/ui/Breadcrumbs/types';
import type { ICompanyFeature } from '@/pages/about/model/types/companyFeatures';
import CompanyFeature from '@/pages/about/ui/CompanyFeature.vue';
import ForBusiness from '@/pages/about/ui/ForBusiness.vue';

const { t } = useI18n();
const localePath = useLocalePath();

const breadcrumbs: Ref<IBreadcrumbs[]> = ref([
  { id: 1, text: t('main'), link: localePath('/') },
  { id: 2, text: t('aboutCompany'), link: '' },
]);

const features: Ref<ICompanyFeature[]> = ref([
  {
    id: 1,
    title: t('missionAndValues'),
    description: t('missionDescription'),
    image: '/images/about/missiya-i-cennosti.webp',
  },
  {
    id: 2,
    title: t('strategy'),
    description: t('strategyDescription'),
    image: '/images/about/strategiya.webp',
  },
  {
    id: 3,
    title: t('history'),
    description: t('historyDescription'),
    image: '/images/about/istoriya.webp',
  },
  {
    id: 4,
    title: t('awardsAndAchievements'),
    description: t('awardsAndAchievementsDescription'),
    image: '/images/about/nagrady-i-dostizheniya.webp',
  },
]);

useSeoMeta({
  title: t('aboutCompany'),
  ogTitle: t('aboutCompany'),
  description: t('aboutCompanyDescription'),
  ogDescription: t('aboutCompanyDescription'),
  ogImage: 'https://resursi-distribution.uz/images/banners/about/about-banner.webp',
});
</script>

<template>
  <div class="about">
    <div class="about__breadcrumbs">
      <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <div class="about__sections-wrapper">
      <section class="about__banner">
        <UIBanner
          :images="{
            desktop: '/images/banners/about/about-banner.webp',
            mobile: '/images/banners/about/about-banner-mobile.webp',
          }"
          is-hide-line-break
        />
      </section>
      <section class="about__content">
        <div class="about__content-item _text">
          <h1 class="about__title">
            {{ t('aboutCompany') }}
          </h1>
          <div class="about__text">
            {{ t('aboutCompanyDescription') }}
          </div>
        </div>
        <div class="about__content-item">
          <NuxtImg src="/images/logo.webp" :alt="t('aboutCompany')" class="about__logo" />
        </div>
      </section>
      <section class="about__features">
        <ul class="about__features-list">
          <li v-for="feature in features" :key="feature.id" class="about__features-item">
            <CompanyFeature :feature="feature" />
          </li>
        </ul>
      </section>
      <section class="about__business">
        <ForBusiness />
      </section>
    </div>
  </div>
</template>

<style lang="less">
.about {
  .container-d();
  .flex-column(@size-x6);

  padding-top: @size-x8;
  padding-bottom: 72px;

  &__sections-wrapper {
    .flex-column(72px);
  }

  &__banner {
    height: 300px;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 132px;
  }

  &__content-item {
    &._text {
      .flex-column(@size-x6);

      max-width: 820px;
    }
  }

  &__logo {
    width: 425px;
    height: 123px;
  }

  &__features-list {
    display: grid;
    gap: @size-x5;
    grid-template-columns: repeat(auto-fill, minmax(47%, 1fr));
  }

  &__title {
    .h1();

    text-transform: uppercase;
  }

  &__text {
    font-size: 18px;
    font-weight: 400;
    line-height: 150%;
    color: @color-dove-gray-secondary;
  }

  @media @1024 {
    .container-m();

    gap: @size-x4;
    padding-top: @size-x6;
    padding-bottom: 48px;

    &__banner {
      height: 160px;
    }

    &__sections-wrapper {
      gap: @size-x6;
    }

    &__content {
      flex-direction: column;
      gap: @size-x5;
      margin-bottom: @size-x5;
    }

    &__content-item {
      &._text {
        gap: @size-x3;
      }
    }

    &__features-list {
      gap: @size-x2;
      grid-template-columns: repeat(1, 1fr);
    }

    &__text {
      font-size: 14px;
      line-height: 130%;
    }

    &__logo {
      width: 225px;
      height: 65px;
    }
  }
}
</style>
